/**
 * `workload` — monday's `ResourceAllocationBoardView` (`specs/M4-V6-workload.md`).
 *
 * One row per resource, one capacity cell per time bucket. Everything about the arithmetic is in
 * the five production rows' `settings_str`, so none of it is guessed — including the three facts
 * a reasonable guess gets wrong (`[V6§2.2]`): `effortColumnIdsByBoardId` is keyed by **column**
 * id, `default_capacity` is a **string** whose `""` means "no capacity" rather than zero, and
 * `capacity_per_resource` is `{}` on every real row.
 */
import { z } from 'zod';
import { defineViewType, normaliseOverDefaults } from '../types.js';
import { mondayShapedSettings } from '../from-monday.js';
import { columnSelectionSchema, EMPTY_COLUMN_SELECTION, isColumnSelected, } from '../column-selection.js';
import { addDays, dayIndex } from '../calendar/events.js';
// The kind's own monday reader, so `normaliseSettings` can recognise an imported blob
// (P-025). Re-exported below as well, which is how every caller outside has always had it.
import { workloadSettingsFromMonday } from './from-monday.js';
export * from './from-monday.js';
export * from './to-monday.js';
export const WORKLOAD_MONDAY_KEYS = new Set([
    'group_by_id',
    'color_by_id',
    'label_by_id',
    'columns',
    'effort_column_type',
    'effortColumnIdsByBoardId',
    'sub_items_display_type',
    'default_capacity',
    'is_default_capacity',
    'capacity_per_resource',
    'weight_calculation',
]);
export const workloadViewSettingsSchema = z.object({
    resourceBy: z.string().nullable(),
    colorBy: z.string().nullable(),
    labelBy: z.string().nullable(),
    dateColumns: columnSelectionSchema,
    effortMode: z.enum(['default-label-count', 'effort_column']),
    effortColumnId: z.string().nullable(),
    subItemsDisplayType: z.literal('items').nullable(),
    defaultCapacity: z.string(),
    isDefaultCapacity: z.boolean(),
    capacityPerResource: z.record(z.string(), z.number()),
    weightCalculation: z.literal('weight_calculation_split').nullable(),
    bucket: z.enum(['week', 'month']),
    collapsedResourceKeys: z.array(z.string()),
    mondayKeys: z.array(z.string()),
    mondayExtras: z.record(z.string(), z.unknown()),
});
/** The shape four of the five production rows share, minus their board-specific column ids. */
export const DEFAULT_WORKLOAD_VIEW_SETTINGS = {
    resourceBy: null,
    colorBy: null,
    labelBy: null,
    dateColumns: EMPTY_COLUMN_SELECTION,
    effortMode: 'default-label-count',
    effortColumnId: null,
    subItemsDisplayType: 'items',
    defaultCapacity: '',
    isDefaultCapacity: true,
    capacityPerResource: {},
    weightCalculation: 'weight_calculation_split',
    bucket: 'week',
    collapsedResourceKeys: [],
    mondayKeys: [],
    mondayExtras: {},
};
/**
 * The Workload draws on its **own** scale, not the Gantt's: `prod-workload`'s week labels sit
 * on a 116 px pitch (16.6 px/day) and its month labels on a ~510 px one (16.8 px/day), so the
 * bucket changes the boundaries and not the density (`[V6§2.1]`).
 */
export const WORKLOAD_PX_PER_DAY = 16.6;
/** Measured on the production Workload (`[V6§2.1]`). */
export const WORKLOAD_METRICS = {
    sidePanelWidth: 264,
    resourceRowHeight: 80,
    avatarSize: 30,
    avatarWrapperSize: 32,
    avatarLeft: 40,
    nameLeft: 76,
    chevronSize: 22,
    chevronLeft: 12,
    headerMajorHeight: 33,
    headerMinorHeight: 32,
    legendHeight: 53,
};
/**
 * The capacity a resource has in one bucket, or **`null`** when it has none.
 *
 * `null` is not zero: three of the five real views store `default_capacity: ""`, which is why
 * they paint no red at all. Treating `""` as 0 would mark every busy person over capacity
 * (`[V6§2.4]`).
 */
export function capacityFor(settings, resourceId) {
    const override = settings.capacityPerResource[resourceId];
    if (typeof override === 'number' && Number.isFinite(override))
        return override;
    const trimmed = settings.defaultCapacity.trim();
    if (trimmed === '')
        return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
}
/** Days in an inclusive span, at least 1. */
export function spanDays(span) {
    return Math.max(1, dayIndex(span.to) - dayIndex(span.from) + 1);
}
/** Days two inclusive spans share. */
export function overlapDays(a, b) {
    const from = Math.max(dayIndex(a.from), dayIndex(b.from));
    const to = Math.min(dayIndex(a.to), dayIndex(b.to));
    return Math.max(0, to - from + 1);
}
/**
 * One item's contribution to one bucket (`[V6§2.3]`).
 *
 * **`default-label-count` counts items**: an item whose span touches the bucket contributes
 * exactly **1**, whole. Splitting a count would make "how many things is this person on this
 * week" answer 0.16 for a month-long task, which is not a count of anything (`[V6§2.3]`, q428).
 *
 * **`effort_column`** contributes the number in the effort column (an empty cell is 0), and
 * `weight_calculation_split` — the only value in the account, and the fallback when the key is
 * absent — divides it over the days of the span, giving the bucket the share for the days it
 * covers: a 10-point, 5-day task overlapping 2 days of a week contributes 4, and the five
 * daily shares sum back to 10.
 */
export function effortInBucket(input) {
    const shared = overlapDays(input.span, input.bucket);
    if (shared === 0)
        return 0;
    if (input.settings.effortMode !== 'effort_column')
        return 1;
    // Absent weighting is monday's default, which is split (`[V6§2.3]`).
    return ((input.effortValue ?? 0) / spanDays(input.span)) * shared;
}
/** The unassigned row's id, so the renderer and the tests agree on one token. */
export const WORKLOAD_UNASSIGNED = '__unassigned__';
/**
 * The whole grid: resources × buckets, with `over` per cell. Pure — the renderer only paints.
 *
 * An item assigned to several people loads **each** of them in full: monday's split is over the
 * item's days, never over its assignees (`weight_calculation_split` is about the span; there is
 * no `…_per_person` value in the account).
 */
export function buildWorkloadGrid(input) {
    const byKey = new Map();
    const resources = [...(input.resourceIds ?? [])];
    const seen = new Set(resources);
    for (const item of input.items) {
        if (item.span === null)
            continue;
        const targets = item.resourceIds.length > 0 ? item.resourceIds : [WORKLOAD_UNASSIGNED];
        for (const resourceId of targets) {
            if (!seen.has(resourceId)) {
                seen.add(resourceId);
                resources.push(resourceId);
            }
            for (const bucket of input.buckets) {
                const load = effortInBucket({
                    settings: input.settings,
                    effortValue: item.effortValue,
                    span: item.span,
                    bucket,
                });
                if (load === 0)
                    continue;
                const key = `${resourceId} ${bucket.key}`;
                const entry = byKey.get(key) ?? { load: 0, itemIds: [] };
                entry.load += load;
                entry.itemIds.push(item.id);
                byKey.set(key, entry);
            }
        }
    }
    const cells = [];
    for (const resourceId of resources) {
        const capacity = capacityFor(input.settings, resourceId);
        for (const bucket of input.buckets) {
            const entry = byKey.get(`${resourceId} ${bucket.key}`);
            const load = entry?.load ?? 0;
            cells.push({
                resourceId,
                bucketKey: bucket.key,
                // Two decimals: a split load is a fraction and the cell prints it.
                load: Math.round(load * 100) / 100,
                capacity,
                over: capacity !== null && load > capacity,
                itemIds: entry?.itemIds ?? [],
            });
        }
    }
    const index = new Map(cells.map((cell) => [`${cell.resourceId} ${cell.bucketKey}`, cell]));
    return {
        buckets: input.buckets,
        resourceIds: resources,
        cells,
        cell: (resourceId, bucketKey) => index.get(`${resourceId} ${bucketKey}`),
    };
}
/** The buckets covering an inclusive range, Monday-first weeks or calendar months. */
export function workloadBuckets(input) {
    const weekStartsOn = input.weekStartsOn ?? 1;
    const out = [];
    let cursor = input.bucket === 'week'
        ? addDays(input.from, -(((((dayIndex(input.from) + 4) % 7) + 7) % 7) - weekStartsOn + 7) % 7)
        : `${input.from.slice(0, 7)}-01`;
    // A degenerate range must still yield one bucket rather than loop forever.
    let guard = 0;
    while (cursor <= input.to && guard < 10_000) {
        guard += 1;
        const next = input.bucket === 'week'
            ? addDays(cursor, 7)
            : (() => {
                const year = Number(cursor.slice(0, 4));
                const month = Number(cursor.slice(5, 7));
                return month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, '0')}-01`;
            })();
        out.push({ key: cursor, from: cursor, to: addDays(next, -1) });
        cursor = next;
    }
    return out;
}
/** The date column an item's span comes from — the workload names exactly one (`[V6§2.2]`). */
export function workloadDateColumnFor(settings, columns) {
    const selected = columns.filter((column) => isColumnSelected(settings.dateColumns, column.id));
    const pool = selected.length > 0 ? selected : columns;
    return pool.find((column) => column.type === 'timeline')?.id ?? pool[0]?.id ?? null;
}
export const workloadView = defineViewType({
    id: 'workload',
    title: 'Workload',
    mondayViewType: 'ResourceAllocationBoardView',
    kind: 'APP',
    settingsSchema: workloadViewSettingsSchema,
    defaultSettings: DEFAULT_WORKLOAD_VIEW_SETTINGS,
    normaliseSettings: (input) => normaliseOverDefaults('workload', workloadViewSettingsSchema, DEFAULT_WORKLOAD_VIEW_SETTINGS, mondayShapedSettings(input, DEFAULT_WORKLOAD_VIEW_SETTINGS, workloadSettingsFromMonday)),
    requiredColumnTypes: [],
    supportsFilters: true,
    supportsSort: false,
    supportsGroupBy: true,
    canBeDefault: true,
    implemented: true,
    plannedIn: null,
    gallery: { order: 12, section: 'explore' },
});
//# sourceMappingURL=index.js.map